import { useEffect, useState } from "react";
import { cn } from "./cn";
import { useI18n } from "../i18n";
import { chain, chainId } from "../lib/wallet";

/**
 * HeroStatsPanel — WO#5 Neon-Editorial v2.
 *
 * Renders the "NETWORK STATUS · LIVE" HUD panel from Claudio's
 * reference — but ANTI-FAKE compliant. Every value below is
 * derived from `/api/v1/grid/stats` (real Phase 1B indexer) or
 * the canonical chain manifest.
 *
 * No invented uptime, no invented transaction count, no invented
 * node count. When the indexer reports `reconciled=true` the panel
 * shows LIVE; on any refresh failure it degrades to a truthful
 * "syncing / offline" state.
 */

interface StatsResponse {
  stats: {
    chain_head: number;
    indexed_through_block: number;
    safe_block: number;
    finalized_block: number;
    next_parcel_id: number;
    total_allocated_slots: number;
    parcel_count: number;
  };
  meta: {
    chain_id: number;
    as_of_block: number;
    reconciled: boolean;
    data_status: string;
  };
}

const TOTAL_CELLS = 1_000_000; // canonical (1000 × 1000)
const POLL_MS = 15_000;

export function HeroStatsPanel({ className }: { className?: string }) {
  const { t } = useI18n();
  const [state, setState] = useState<
    | { kind: "loading" }
    | { kind: "ready"; data: StatsResponse }
    | { kind: "error"; message: string }
  >({ kind: "loading" });

  useEffect(() => {
    let alive = true;
    const controller = new AbortController();

    const load = async () => {
      try {
        const resp = await fetch("/api/v1/grid/stats", {
          signal: controller.signal,
        });
        if (!alive) return;
        if (!resp.ok) {
          setState({ kind: "error", message: `HTTP ${resp.status}` });
          return;
        }
        const body = (await resp.json()) as StatsResponse;
        if (!alive) return;
        setState({ kind: "ready", data: body });
      } catch (e: unknown) {
        if (!alive) return;
        if ((e as { name?: string })?.name === "AbortError") return;
        setState({
          kind: "error",
          message: (e as Error)?.message ?? "network",
        });
      }
    };
    void load();
    const iv = window.setInterval(load, POLL_MS);
    return () => {
      alive = false;
      controller.abort();
      window.clearInterval(iv);
    };
  }, []);

  const live = state.kind === "ready" && state.data.meta.reconciled;
  const chainName = chain?.name ?? `Chain ${chainId}`;

  const parcelCount =
    state.kind === "ready" ? state.data.stats.parcel_count : null;
  const allocated =
    state.kind === "ready" ? state.data.stats.total_allocated_slots : null;
  const claimedPct =
    allocated != null ? (allocated / TOTAL_CELLS) * 100 : null;
  const indexedBlock =
    state.kind === "ready" ? state.data.stats.indexed_through_block : null;
  const finalized =
    state.kind === "ready" ? state.data.stats.finalized_block : null;

  return (
    <aside
      data-testid="hero-stats-panel"
      className={cn(
        "neon-glass",
        "px-5 py-5",
        "w-full",
        "flex flex-col gap-4",
        className,
      )}
    >
      {/* Header */}
      <header className="flex items-center justify-between">
        <span className="font-display text-[10.5px] font-semibold tracking-[0.24em] uppercase text-text-secondary">
          {t("grid.stats.networkStatus")}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span
            aria-hidden
            className={cn(
              "h-1.5 w-1.5 rounded-full dot-pulse",
              live ? "bg-neon-teal shadow-glow-teal-sm" : "bg-neon-coral shadow-glow-coral-sm",
            )}
          />
          <span
            className={cn(
              "font-display text-[10px] font-semibold tracking-[0.22em] uppercase",
              live ? "text-neon-teal" : "text-neon-coral",
            )}
            data-testid="hero-stats-live-indicator"
          >
            {live ? t("grid.stats.live") : state.kind === "error" ? t("grid.stats.offline") : t("grid.stats.syncing")}
          </span>
        </span>
      </header>

      {/* Row: chain */}
      <StatRow
        label={t("grid.stats.chain")}
        value={chainName}
        valueClassName="font-display text-[15px] font-semibold text-text-primary"
        testid="hero-stats-chain"
      />

      {/* Row: parcels claimed */}
      <StatRow
        label={t("grid.stats.parcelsClaimed")}
        value={parcelCount == null ? "—" : parcelCount.toLocaleString("en-US")}
        subValue={parcelCount == null ? undefined : t("grid.stats.onChain")}
        big
        testid="hero-stats-parcels"
      />

      {/* Row: cells allocated of 1,000,000 */}
      <StatRow
        label={t("grid.stats.cellsAllocated")}
        value={
          allocated == null
            ? "—"
            : `${allocated.toLocaleString("en-US")} / 1,000,000`
        }
        subValue={
          claimedPct == null
            ? undefined
            : t("grid.stats.finiteWorld", { pct: claimedPct.toFixed(4) })
        }
        testid="hero-stats-slots"
      />

      {/* Row: indexer block */}
      <StatRow
        label={t("grid.stats.indexerHead")}
        value={
          indexedBlock == null
            ? "—"
            : `#${indexedBlock.toLocaleString("en-US")}`
        }
        subValue={
          finalized == null
            ? undefined
            : t("grid.stats.finalized", { block: finalized.toLocaleString("en-US") })
        }
        mono
        testid="hero-stats-block"
      />
    </aside>
  );
}

function StatRow({
  label,
  value,
  subValue,
  big = false,
  mono = false,
  testid,
  valueClassName,
}: {
  label: string;
  value: string;
  subValue?: string;
  big?: boolean;
  mono?: boolean;
  testid?: string;
  valueClassName?: string;
}) {
  return (
    <div data-testid={testid} className="flex flex-col gap-1">
      <div className="font-display text-[10px] font-medium tracking-[0.22em] uppercase text-text-muted">
        {label}
      </div>
      <div
        className={cn(
          "text-text-primary leading-tight",
          valueClassName
            ? valueClassName
            : big
              ? "font-hero text-[22px] tracking-[0.02em]"
              : mono
                ? "font-mono text-[14px]"
                : "font-display text-[15px] font-semibold",
        )}
      >
        {value}
      </div>
      {subValue && (
        <div
          className={cn(
            "text-text-secondary text-[11px]",
            mono ? "font-mono" : "font-display",
          )}
        >
          {subValue}
        </div>
      )}
    </div>
  );
}
